/* crm-analytics-p6.jsx — Sales Analytics (5-tab product with global filters)
   Tabs: KPIs | Pipeline | Forecast | Team | Activities
   Provides: AnalyticsP6View */

const { useState, useEffect, useCallback, useRef } = React;

import * as api from '../../api/analytics-p6.api.js';

// ─── Format helpers ───────────────────────────────────────────────────────────

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000)     return '$' + (v / 1_000).toFixed(0) + 'K';
  return '$' + Math.round(v);
};
const fmtPct   = (n) => Math.round(Number(n || 0)) + '%';
const fmtNum   = (n) => Number(n || 0).toLocaleString();
const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmtDateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Date preset resolution ───────────────────────────────────────────────────

const DATE_PRESETS = [
  { key: 'today',         label: 'Today' },
  { key: 'yesterday',     label: 'Yesterday' },
  { key: 'last7d',        label: 'Last 7 days' },
  { key: 'last15d',       label: 'Last 15 days' },
  { key: 'last30d',       label: 'Last 30 days' },
  { key: 'last90d',       label: 'Last 90 days' },
  { key: 'thisWeek',      label: 'This week' },
  { key: 'lastWeek',      label: 'Last week' },
  { key: 'thisMonth',     label: 'This month' },
  { key: 'lastMonth',     label: 'Last month' },
  { key: 'thisQuarter',   label: 'This quarter' },
  { key: 'lastQuarter',   label: 'Last quarter' },
  { key: 'thisYear',      label: 'This year' },
  { key: 'lastYear',      label: 'Last year' },
  { key: 'custom',        label: 'Custom range' },
];

function resolvePreset(preset) {
  const now = new Date();
  const t = (d) => toDateStr(d);
  switch (preset) {
    case 'today':       { const d = t(now); return { from: d, to: d }; }
    case 'yesterday':   { const d = new Date(now); d.setDate(d.getDate()-1); const s=t(d); return {from:s,to:s}; }
    case 'last7d':      { const f=new Date(now); f.setDate(f.getDate()-6); return {from:t(f),to:t(now)}; }
    case 'last15d':     { const f=new Date(now); f.setDate(f.getDate()-14); return {from:t(f),to:t(now)}; }
    case 'last30d':     { const f=new Date(now); f.setDate(f.getDate()-29); return {from:t(f),to:t(now)}; }
    case 'last90d':     { const f=new Date(now); f.setDate(f.getDate()-89); return {from:t(f),to:t(now)}; }
    case 'thisWeek':    { const f=new Date(now); f.setDate(f.getDate()-f.getDay()); return {from:t(f),to:t(now)}; }
    case 'lastWeek':    { const end=new Date(now); end.setDate(end.getDate()-end.getDay()-1); const start=new Date(end); start.setDate(start.getDate()-6); return {from:t(start),to:t(end)}; }
    case 'thisMonth':   { const f=new Date(now.getFullYear(),now.getMonth(),1); return {from:t(f),to:t(now)}; }
    case 'lastMonth':   { const e=new Date(now.getFullYear(),now.getMonth(),0); const f=new Date(e.getFullYear(),e.getMonth(),1); return {from:t(f),to:t(e)}; }
    case 'thisQuarter': { const q=Math.floor(now.getMonth()/3); const f=new Date(now.getFullYear(),q*3,1); return {from:t(f),to:t(now)}; }
    case 'lastQuarter': { const q=Math.floor(now.getMonth()/3); const pq=q===0?3:q-1; const yr=q===0?now.getFullYear()-1:now.getFullYear(); const f=new Date(yr,pq*3,1); const e=new Date(yr,pq*3+3,0); return {from:t(f),to:t(e)}; }
    case 'thisYear':    { return {from:`${now.getFullYear()}-01-01`,to:t(now)}; }
    case 'lastYear':    { const y=now.getFullYear()-1; return {from:`${y}-01-01`,to:`${y}-12-31`}; }
    default:            return {from:t(new Date(now.getFullYear(),now.getMonth(),now.getDate()-29)),to:t(now)};
  }
}

// ─── Primitive components ─────────────────────────────────────────────────────

const S = {
  card:     { background:'var(--nx-surface-alt)', border:'1px solid var(--nx-border)', borderRadius:10, padding:'14px 18px' },
  muted:    { color:'var(--nx-text-muted)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:4 },
  bigVal:   { fontSize:22, fontWeight:800, color:'var(--nx-text)' },
  sub:      { fontSize:11, color:'var(--nx-text-muted)', marginTop:2 },
  label14:  { fontWeight:700, fontSize:14, marginBottom:16 },
  th:       { padding:'6px 12px', color:'var(--nx-text-muted)', fontWeight:600, fontSize:11 },
  td:       { padding:'8px 12px', fontSize:13 },
  loadingBox: { padding:24, color:'var(--nx-text-muted)', fontSize:13 },
  errorBox:   { padding:24, color:'var(--nx-error,#ef4444)', fontSize:13 },
  emptyBox:   { padding:'32px 24px', textAlign:'center', color:'var(--nx-text-muted)', fontSize:13 },
  pill: (active) => ({
    padding:'3px 10px', borderRadius:6, border:'1px solid var(--nx-border)',
    background: active ? 'var(--nx-accent)' : 'transparent',
    color: active ? '#fff' : 'var(--nx-text)', cursor:'pointer', fontSize:11, fontWeight:500,
  }),
};

const KpiCard = ({ label, value, sub, accent }) => (
  <div style={{ ...S.card, flex:'1 1 140px', minWidth:130 }}>
    <div style={S.muted}>{label}</div>
    <div style={{ ...S.bigVal, color: accent || 'var(--nx-text)' }}>{value}</div>
    {sub && <div style={S.sub}>{sub}</div>}
  </div>
);

const Loading = ({ msg = 'Loading…' }) => <div style={S.loadingBox}>{msg}</div>;
const ErrorMsg = ({ msg }) => <div style={S.errorBox}>{msg}</div>;
const Empty = ({ title, detail }) => (
  <div style={S.emptyBox}>
    <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>{title}</div>
    {detail && <div style={{ maxWidth:380, margin:'0 auto', lineHeight:1.5 }}>{detail}</div>}
  </div>
);

// ─── Global filter bar ────────────────────────────────────────────────────────

const FilterBar = ({ filters, setFilters, pipelines, members }) => {
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [customFrom, setCustomFrom] = useState(filters.dateFrom || '');
  const [customTo,   setCustomTo]   = useState(filters.dateTo   || '');
  const menuRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowDateMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const activePresetLabel = () => {
    if (filters.datePreset === 'custom') {
      const f = fmtDateLabel(filters.dateFrom);
      const t = fmtDateLabel(filters.dateTo);
      if (f && t) return `${f} – ${t}`;
      return 'Custom range';
    }
    return DATE_PRESETS.find(p => p.key === filters.datePreset)?.label || 'Last 30 days';
  };

  const selectPreset = (key) => {
    if (key === 'custom') {
      setFilters(f => ({ ...f, datePreset: 'custom' }));
    } else {
      const { from, to } = resolvePreset(key);
      setFilters(f => ({ ...f, datePreset: key, dateFrom: from, dateTo: to }));
      setShowDateMenu(false);
    }
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setFilters(f => ({ ...f, datePreset: 'custom', dateFrom: customFrom, dateTo: customTo }));
    setShowDateMenu(false);
  };

  const sel = { fontSize:12, padding:'4px 8px', border:'1px solid var(--nx-border)', borderRadius:6, background:'var(--nx-surface-alt)', color:'var(--nx-text)', cursor:'pointer' };

  return (
    <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:8, padding:'10px 20px', borderBottom:'1px solid var(--nx-border)', background:'var(--nx-surface)' }}>
      {/* Date range */}
      <div style={{ position:'relative' }} ref={menuRef}>
        <button type="button" onClick={() => setShowDateMenu(v => !v)} style={{ ...sel, display:'flex', alignItems:'center', gap:6, minWidth:160 }}>
          <span style={{ fontSize:11 }}>📅</span>
          <span>{activePresetLabel()}</span>
          <span style={{ marginLeft:'auto', opacity:0.5 }}>▾</span>
        </button>
        {showDateMenu && (
          <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:200, background:'var(--nx-surface-alt)', border:'1px solid var(--nx-border)', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', width:220, padding:8 }}>
            {DATE_PRESETS.filter(p => p.key !== 'custom').map(p => (
              <button type="button" key={p.key} onClick={() => selectPreset(p.key)} style={{
                display:'block', width:'100%', textAlign:'left', padding:'6px 10px', border:'none', borderRadius:5, cursor:'pointer',
                background: filters.datePreset === p.key ? 'var(--nx-accent)' : 'transparent',
                color: filters.datePreset === p.key ? '#fff' : 'var(--nx-text)', fontSize:12,
              }}>{p.label}</button>
            ))}
            <div style={{ height:1, background:'var(--nx-border)', margin:'6px 0' }} />
            <button type="button" onClick={() => selectPreset('custom')} style={{
              display:'block', width:'100%', textAlign:'left', padding:'6px 10px', border:'none', borderRadius:5, cursor:'pointer',
              background: filters.datePreset === 'custom' ? 'var(--nx-accent)' : 'transparent',
              color: filters.datePreset === 'custom' ? '#fff' : 'var(--nx-text)', fontSize:12,
            }}>Custom range…</button>
            {filters.datePreset === 'custom' && (
              <div style={{ padding:'8px 6px', borderTop:'1px solid var(--nx-border)', marginTop:4 }}>
                <div style={{ marginBottom:6 }}>
                  <div style={{ fontSize:10, color:'var(--nx-text-muted)', marginBottom:2 }}>FROM</div>
                  <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                    style={{ width:'100%', padding:'4px 6px', border:'1px solid var(--nx-border)', borderRadius:5, background:'var(--nx-surface)', color:'var(--nx-text)', fontSize:12 }} />
                </div>
                <div style={{ marginBottom:8 }}>
                  <div style={{ fontSize:10, color:'var(--nx-text-muted)', marginBottom:2 }}>TO</div>
                  <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} min={customFrom}
                    style={{ width:'100%', padding:'4px 6px', border:'1px solid var(--nx-border)', borderRadius:5, background:'var(--nx-surface)', color:'var(--nx-text)', fontSize:12 }} />
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button type="button" onClick={applyCustom} disabled={!customFrom||!customTo} style={{ flex:1, padding:'5px 0', borderRadius:5, border:'none', background:'var(--nx-accent)', color:'#fff', fontSize:12, cursor:'pointer', opacity:(!customFrom||!customTo)?0.5:1 }}>Apply</button>
                  <button type="button" onClick={() => setShowDateMenu(false)} style={{ flex:1, padding:'5px 0', borderRadius:5, border:'1px solid var(--nx-border)', background:'transparent', color:'var(--nx-text)', fontSize:12, cursor:'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pipeline filter */}
      {pipelines?.length > 0 && (
        <select value={filters.pipelineId || ''} onChange={e => setFilters(f => ({...f, pipelineId: e.target.value || null}))} style={sel}>
          <option value="">All Pipelines</option>
          {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}

      {/* Owner filter */}
      {members?.length > 0 && (
        <select value={filters.ownerId || ''} onChange={e => setFilters(f => ({...f, ownerId: e.target.value || null}))} style={sel}>
          <option value="">All Owners</option>
          {members.map(m => <option key={m.userId} value={m.userId}>{m.name}</option>)}
        </select>
      )}

      {/* Active filter summary */}
      <div style={{ marginLeft:'auto', fontSize:11, color:'var(--nx-text-muted)' }}>
        {activePresetLabel()}
        {filters.pipelineId && pipelines && ` · ${pipelines.find(p=>p.id===filters.pipelineId)?.name||''}`}
        {filters.ownerId && members && ` · ${members.find(m=>m.userId===filters.ownerId)?.name||''}`}
      </div>
    </div>
  );
};

// ─── Tab 1: KPIs ──────────────────────────────────────────────────────────────

const KpiTab = ({ workspaceId, filters }) => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const params = { from: filters.dateFrom, to: filters.dateTo };
    if (filters.ownerId)    params.ownerId    = filters.ownerId;
    if (filters.pipelineId) params.pipelineId = filters.pipelineId;
    api.getKpis(workspaceId, params)
      .then(setKpis)
      .catch(e => setError(e.message || 'Failed to load KPIs'))
      .finally(() => setLoading(false));
  }, [workspaceId, filters.dateFrom, filters.dateTo, filters.ownerId, filters.pipelineId]);

  if (loading) return <Loading msg="Loading KPIs…" />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!kpis)   return null;

  return (
    <div>
      <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Key Performance Indicators</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:24 }}>
        <KpiCard label="Pipeline Value"  value={fmtMoney(kpis.pipeline?.value)} sub={`${fmtNum(kpis.pipeline?.count)} open deals`} accent="var(--nx-accent)" />
        <KpiCard label="Won Revenue"     value={fmtMoney(kpis.won?.value)}      sub={`${fmtNum(kpis.won?.count)} deals won`}       accent="var(--nx-success,#22c55e)" />
        <KpiCard label="Lost Value"      value={fmtMoney(kpis.lost?.value)}     sub={`${fmtNum(kpis.lost?.count)} deals lost`}      accent="var(--nx-error,#ef4444)" />
        <KpiCard label="Win Rate"        value={fmtPct(kpis.winRate)}           sub={`${fmtNum(kpis.newDeals)} new deals`} />
        <KpiCard label="Avg Deal Size"   value={fmtMoney(kpis.avgDealSize)}     sub={kpis.won?.count > 0 ? `based on ${kpis.won.count} won` : 'no won deals yet'} />
        <KpiCard label="Activities"      value={fmtNum(kpis.activities)}        sub={`${fmtNum(kpis.emails)} emails · ${fmtNum(kpis.meetings)} meetings`} />
        <KpiCard label="Open Tasks"      value={fmtNum(kpis.openTasks)}         sub={`${fmtNum(kpis.tasks)} created in period`} />
        <KpiCard label="People"          value={fmtNum(kpis.contacts?.people)}  sub={`${fmtNum(kpis.contacts?.companies)} companies`} />
      </div>
      <div style={{ fontSize:11, color:'var(--nx-text-muted)', marginTop:8 }}>
        Pipeline Value reflects all open deals (current snapshot). Won/Lost/Activities apply the selected date range.
      </div>
    </div>
  );
};

// ─── Tab 2: Pipeline ──────────────────────────────────────────────────────────

const PipelineTab = ({ workspaceId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const params = {};
    if (filters.pipelineId) params.pipelineId = filters.pipelineId;
    if (filters.ownerId)    params.ownerId    = filters.ownerId;
    if (filters.dateFrom)   params.from = filters.dateFrom;
    if (filters.dateTo)     params.to   = filters.dateTo;
    api.getFunnel(workspaceId, params)
      .then(setData)
      .catch(e => setError(e.message || 'Failed to load pipeline'))
      .finally(() => setLoading(false));
  }, [workspaceId, filters.dateFrom, filters.dateTo, filters.pipelineId, filters.ownerId]);

  if (loading) return <Loading msg="Loading pipeline…" />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data?.funnel?.length) return <Empty title="No pipeline stages." detail="No stages are configured for this workspace." />;

  const { funnel, summary } = data;
  const maxCount = Math.max(...funnel.map(s => s.count), 1);

  return (
    <div>
      {/* Summary row */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
        <KpiCard label="Open Pipeline"   value={fmtMoney(summary.totalPipeline)}  sub={`${fmtNum(summary.totalDeals)} deals`}       accent="var(--nx-accent)" />
        <KpiCard label="Weighted"        value={fmtMoney(summary.totalWeighted)}  sub="probability-weighted" />
        <KpiCard label="Won Revenue"     value={fmtMoney(summary.totalWon)}       sub={`${fmtNum(summary.totalWonDeals)} deals won`} accent="var(--nx-success,#22c55e)" />
        <KpiCard label="Lost"            value="" sub={`${fmtNum(summary.totalLostDeals)} deals lost`} accent="var(--nx-error,#ef4444)" />
      </div>

      {/* Stage breakdown */}
      <div style={S.label14}>Pipeline by Stage</div>
      <div style={{ marginBottom:20 }}>
        {funnel.map(stage => {
          let stageBarColor = 'var(--nx-accent)';
          if (stage.stageKind === 'WON')  stageBarColor = 'var(--nx-success,#22c55e)';
          if (stage.stageKind === 'LOST') stageBarColor = 'var(--nx-error,#ef4444)';
          return (
          <div key={stage.stageId} style={{ marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:3 }}>
              <span style={{ fontSize:12, fontWeight:600, width:140, flexShrink:0 }}>{stage.stageName}</span>
              <div style={{ flex:1, height:22, background:'var(--nx-border)', borderRadius:5, overflow:'hidden' }}>
                <div style={{
                  height:'100%',
                  width:`${Math.max(stage.count > 0 ? 6 : 0, Math.round((stage.count / maxCount) * 100))}%`,
                  background: stageBarColor,
                  borderRadius:5, display:'flex', alignItems:'center', paddingLeft:8, transition:'width 0.3s',
                }}>
                  {stage.count > 0 && <span style={{ fontSize:11, color:'#fff', fontWeight:700 }}>{stage.count}</span>}
                </div>
              </div>
              <span style={{ fontSize:12, width:76, textAlign:'right' }}>{fmtMoney(stage.value)}</span>
              <span style={{ fontSize:11, color:'var(--nx-text-muted)', width:38, textAlign:'right' }}>{stage.probability}%</span>
            </div>
          </div>
          );
        })}
      </div>

      {/* Stage table */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:'1px solid var(--nx-border)' }}>
              {['Stage','Deals','Pipeline','Weighted','Won','Lost'].map(h => (
                <th key={h} style={{ ...S.th, textAlign: h==='Stage' ? 'left' : 'right' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {funnel.map(s => (
              <tr key={s.stageId} style={{ borderBottom:'1px solid var(--nx-border)' }}>
                <td style={{ ...S.td, fontWeight:600 }}>{s.stageName}</td>
                <td style={{ ...S.td, textAlign:'right' }}>{fmtNum(s.count)}</td>
                <td style={{ ...S.td, textAlign:'right' }}>{fmtMoney(s.value)}</td>
                <td style={{ ...S.td, textAlign:'right', color:'var(--nx-text-muted)' }}>{fmtMoney(s.weightedValue)}</td>
                <td style={{ ...S.td, textAlign:'right', color:'var(--nx-success,#22c55e)' }}>{fmtNum(s.wonCount)}</td>
                <td style={{ ...S.td, textAlign:'right', color:'var(--nx-error,#ef4444)' }}>{fmtNum(s.lostCount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Tab 3: Forecast ──────────────────────────────────────────────────────────

const ForecastTab = ({ workspaceId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const params = {};
    if (filters.dateFrom)   params.from = filters.dateFrom;
    if (filters.dateTo)     params.to   = filters.dateTo;
    if (filters.pipelineId) params.pipelineId = filters.pipelineId;
    api.getForecast(workspaceId, params)
      .then(setData)
      .catch(e => setError(e.message || 'Failed to load forecast'))
      .finally(() => setLoading(false));
  }, [workspaceId, filters.dateFrom, filters.dateTo, filters.pipelineId]);

  if (loading) return <Loading msg="Loading forecast…" />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data)   return null;

  if (data.hasNoCloseDates) {
    const dc = data.activePipelineDeals;
    const dcSuffix = dc !== 1 ? 's are' : ' is';
    const dcPhrase = dc > 0 ? `${dc} deal${dcSuffix} in your pipeline but none have` : 'No deals have';
    const noCloseDateDetail = `${dcPhrase} an expected close date in the selected period. Set close dates on deals to enable revenue forecasting.`;
    return (
      <div>
        <div style={S.label14}>Revenue Forecast</div>
        <Empty title="No expected close dates set" detail={noCloseDateDetail} />
      </div>
    );
  }

  const { forecasts, summary } = data;
  const hasAnyPipeline = forecasts.some(f => f.pipelineValue > 0 || f.wonValue > 0);

  return (
    <div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
        <KpiCard label="Open Pipeline"     value={fmtMoney(summary.totalPipeline)} sub="deals with close dates" accent="var(--nx-accent)" />
        <KpiCard label="Weighted Forecast" value={fmtMoney(summary.totalWeighted)} sub="probability-adjusted" />
        <KpiCard label="Won Revenue"       value={fmtMoney(summary.totalWon)}      sub="closed in period" accent="var(--nx-success,#22c55e)" />
      </div>

      <div style={S.label14}>Monthly Forecast</div>

      {!hasAnyPipeline && (
        <div style={{ ...S.emptyBox, marginBottom:12 }}>
          No deals with expected close dates fall within the selected date range.
        </div>
      )}

      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:'1px solid var(--nx-border)' }}>
              {['Period','Pipeline','Weighted','Won','Lost','Deals'].map(h => (
                <th key={h} style={{ ...S.th, textAlign: h==='Period' ? 'left' : 'right' }}>{h}</th>
              ))}
              <th style={{ ...S.th }}>Bar</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const maxV = Math.max(...forecasts.map(x => x.pipelineValue), 1);
              return forecasts.map(f => (
                <tr key={f.label} style={{ borderBottom:'1px solid var(--nx-border)' }}>
                  <td style={{ ...S.td, fontWeight:600 }}>{f.label}</td>
                  <td style={{ ...S.td, textAlign:'right' }}>{fmtMoney(f.pipelineValue)}</td>
                  <td style={{ ...S.td, textAlign:'right', color:'var(--nx-accent)' }}>{fmtMoney(f.weightedValue)}</td>
                  <td style={{ ...S.td, textAlign:'right', color:'var(--nx-success,#22c55e)' }}>{fmtMoney(f.wonValue)}</td>
                  <td style={{ ...S.td, textAlign:'right', color:'var(--nx-error,#ef4444)' }}>{fmtMoney(f.lostValue)}</td>
                  <td style={{ ...S.td, textAlign:'right' }}>{fmtNum(f.pipelineCount)}</td>
                  <td style={{ ...S.td, width:120 }}>
                    <div style={{ height:8, background:'var(--nx-border)', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${Math.round((f.pipelineValue/maxV)*100)}%`, background:'var(--nx-accent)', borderRadius:4 }} />
                    </div>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Tab 4: Team ──────────────────────────────────────────────────────────────

const TeamTab = ({ workspaceId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const params = {};
    if (filters.dateFrom)   params.from = filters.dateFrom;
    if (filters.dateTo)     params.to   = filters.dateTo;
    if (filters.pipelineId) params.pipelineId = filters.pipelineId;
    api.getTeamPerformance(workspaceId, params)
      .then(setData)
      .catch(e => setError(e.message || 'Failed to load team data'))
      .finally(() => setLoading(false));
  }, [workspaceId, filters.dateFrom, filters.dateTo, filters.pipelineId]);

  if (loading) return <Loading msg="Loading team performance…" />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data)   return null;

  const { team, unassigned } = data;
  const hasUnassigned = unassigned?.count > 0;

  return (
    <div>
      <div style={S.label14}>Team Performance</div>

      {team.length === 0 && !hasUnassigned && (
        <Empty
          title="No team performance data"
          detail="No deals have an assigned owner in this workspace. Assign owners to deals to see per-member analytics."
        />
      )}

      {team.length === 0 && hasUnassigned && (
        <Empty
          title="No assigned deals in this period"
          detail={`${unassigned.count} deal${unassigned.count > 1 ? 's' : ''} (${fmtMoney(unassigned.value)}) ${unassigned.count > 1 ? 'are' : 'is'} in the pipeline without an assigned owner. Assign deals to owners to see per-member performance.`}
        />
      )}

      {team.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--nx-border)' }}>
                {['Owner','Pipeline','Active','Won Deals','Lost','Win Rate','Won Revenue','Avg Deal','Activities'].map(h => (
                  <th key={h} style={{ ...S.th, textAlign: h==='Owner' ? 'left' : 'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {team.map(m => (
                <tr key={m.userId} style={{ borderBottom:'1px solid var(--nx-border)' }}>
                  <td style={{ ...S.td, fontWeight:600 }}>
                    {m.name}
                    {m.email && <div style={{ fontSize:10, color:'var(--nx-text-muted)', fontWeight:400 }}>{m.email}</div>}
                  </td>
                  <td style={{ ...S.td, textAlign:'right', color:'var(--nx-accent)' }}>{fmtMoney(m.pipelineValue)}</td>
                  <td style={{ ...S.td, textAlign:'right' }}>{fmtNum(m.activeDeals)}</td>
                  <td style={{ ...S.td, textAlign:'right', color:'var(--nx-success,#22c55e)' }}>{fmtNum(m.wonDeals)}</td>
                  <td style={{ ...S.td, textAlign:'right', color:'var(--nx-error,#ef4444)' }}>{fmtNum(m.lostDeals)}</td>
                  <td style={{ ...S.td, textAlign:'right' }}>{fmtPct(m.winRate)}</td>
                  <td style={{ ...S.td, textAlign:'right', color:'var(--nx-success,#22c55e)' }}>{fmtMoney(m.wonValue)}</td>
                  <td style={{ ...S.td, textAlign:'right' }}>{fmtMoney(m.avgDealSize)}</td>
                  <td style={{ ...S.td, textAlign:'right' }}>{fmtNum(m.activities)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasUnassigned && team.length > 0 && (
        <div style={{ marginTop:12, padding:'10px 14px', background:'var(--nx-surface-alt)', border:'1px solid var(--nx-border)', borderRadius:8, fontSize:12, color:'var(--nx-text-muted)' }}>
          {unassigned.count} unassigned deal{unassigned.count > 1 ? 's' : ''} ({fmtMoney(unassigned.value)}) not reflected above.
        </div>
      )}
    </div>
  );
};

// ─── Tab 5: Activities ────────────────────────────────────────────────────────

const ActivitiesTab = ({ workspaceId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const params = {};
    if (filters.dateFrom) params.from = filters.dateFrom;
    if (filters.dateTo)   params.to   = filters.dateTo;
    if (filters.ownerId)  params.ownerId = filters.ownerId;
    api.getActivities(workspaceId, params)
      .then(setData)
      .catch(e => setError(e.message || 'Failed to load activities'))
      .finally(() => setLoading(false));
  }, [workspaceId, filters.dateFrom, filters.dateTo, filters.ownerId]);

  if (loading) return <Loading msg="Loading activities…" />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data)   return null;

  const { total, byType, trend, byOwner, tasks, ownerFiltered } = data;

  const typeLabel = (t) => t.replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase());

  const maxTrend  = Math.max(...(trend || []).map(r => r.count), 1);
  const maxByType = Math.max(...(byType || []).map(r => r.count), 1);

  return (
    <div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
        <KpiCard label="Total Activities"   value={fmtNum(total)} sub={`in selected period${ownerFiltered?' (owner-filtered deals)':''}`} accent="var(--nx-accent)" />
        <KpiCard label="Open Tasks"         value={fmtNum(tasks?.open)} sub={`${fmtNum(tasks?.overdue)} overdue`} accent={tasks?.overdue > 0 ? 'var(--nx-error,#ef4444)' : undefined} />
        <KpiCard label="Completed Tasks"    value={fmtNum(tasks?.completed)} sub={`${fmtNum(tasks?.total)} total tasks`} accent="var(--nx-success,#22c55e)" />
        <KpiCard label="Tasks This Period"  value={fmtNum(tasks?.createdInPeriod)} sub="created in range" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>

        {/* Activity by type */}
        <div>
          <div style={S.label14}>Activity by Type</div>
          {byType.length === 0 && <div style={{ fontSize:12, color:'var(--nx-text-muted)' }}>No activities in this period.</div>}
          {byType.map(r => (
            <div key={r.type} style={{ marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                <span style={{ fontSize:12, fontWeight:500, flex:1 }}>{typeLabel(r.type)}</span>
                <span style={{ fontSize:12, fontWeight:700, width:50, textAlign:'right' }}>{fmtNum(r.count)}</span>
              </div>
              <div style={{ height:6, background:'var(--nx-border)', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${Math.round((r.count/maxByType)*100)}%`, background:'var(--nx-accent)', borderRadius:3 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Activity trend */}
        <div>
          <div style={S.label14}>Daily Trend</div>
          {trend.length === 0 && <div style={{ fontSize:12, color:'var(--nx-text-muted)' }}>No activity data for this period.</div>}
          {trend.length > 0 && (
            <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:80 }}>
              {trend.map(r => (
                <div key={r.day} title={`${r.count} activities`}
                  style={{ flex:1, minWidth:2, height:`${Math.round((r.count/maxTrend)*100)}%`, background:'var(--nx-accent)', borderRadius:'2px 2px 0 0', opacity:0.8 }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity by owner */}
      {byOwner.length > 0 && (
        <div>
          <div style={S.label14}>Activity by Owner (via deal ownership)</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--nx-border)' }}>
                  <th style={{ ...S.th, textAlign:'left' }}>Owner</th>
                  <th style={{ ...S.th, textAlign:'right' }}>Activities</th>
                  <th style={{ ...S.th }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {byOwner.map(r => (
                  <tr key={r.userId} style={{ borderBottom:'1px solid var(--nx-border)' }}>
                    <td style={{ ...S.td, fontWeight:600 }}>{r.name}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{fmtNum(r.count)}</td>
                    <td style={{ ...S.td, width:120 }}>
                      <div style={{ height:8, background:'var(--nx-border)', borderRadius:4, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${Math.round((r.count/Math.max(...byOwner.map(x=>x.count),1))*100)}%`, background:'var(--nx-accent)', borderRadius:4 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize:11, color:'var(--nx-text-muted)', marginTop:8 }}>
            Only activities linked to deals with an assigned owner are shown. Unlinked activities are excluded from this breakdown.
          </div>
        </div>
      )}

      {ownerFiltered && (
        <div style={{ marginTop:10, fontSize:11, color:'var(--nx-text-muted)' }}>
          Owner filter: showing only activities linked to deals owned by the selected member.
        </div>
      )}
    </div>
  );
};

// ─── Tab bar ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'kpis',       label: 'KPIs' },
  { id: 'pipeline',   label: 'Pipeline' },
  { id: 'forecast',   label: 'Forecast' },
  { id: 'team',       label: 'Team' },
  { id: 'activities', label: 'Activities' },
];

// ─── Root view ────────────────────────────────────────────────────────────────

const AnalyticsP6View = ({ workspaceId }) => {
  const [tab, setTab] = useState('kpis');
  const [pipelines, setPipelines] = useState([]);
  const [members,   setMembers]   = useState([]);

  const initRange = resolvePreset('last30d');
  const [filters, setFilters] = useState({
    datePreset: 'last30d',
    dateFrom:   initRange.from,
    dateTo:     initRange.to,
    pipelineId: null,
    ownerId:    null,
  });

  // Load filter dropdowns once
  useEffect(() => {
    api.getPipelines(workspaceId)
      .then(d => setPipelines(d?.pipelines || []))
      .catch(() => {});
    api.getMembers(workspaceId)
      .then(d => setMembers(d?.members || []))
      .catch(() => {});
  }, [workspaceId]);

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>
      {/* Filter bar */}
      <FilterBar filters={filters} setFilters={setFilters} pipelines={pipelines} members={members} />

      {/* Tab bar */}
      <div style={{ display:'flex', gap:0, borderBottom:'1px solid var(--nx-border)', padding:'0 20px', background:'var(--nx-surface)', flexShrink:0 }}>
        {TABS.map(t => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} style={{
            padding:'10px 16px', border:'none', background:'transparent',
            borderBottom: tab === t.id ? '2px solid var(--nx-accent)' : '2px solid transparent',
            color: tab === t.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
            fontWeight: tab === t.id ? 700 : 500, cursor:'pointer', fontSize:13, marginBottom:-1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px 20px' }}>
        {tab === 'kpis'       && <KpiTab        workspaceId={workspaceId} filters={filters} />}
        {tab === 'pipeline'   && <PipelineTab   workspaceId={workspaceId} filters={filters} />}
        {tab === 'forecast'   && <ForecastTab   workspaceId={workspaceId} filters={filters} />}
        {tab === 'team'       && <TeamTab       workspaceId={workspaceId} filters={filters} />}
        {tab === 'activities' && <ActivitiesTab workspaceId={workspaceId} filters={filters} />}
      </div>
    </div>
  );
};

window.AnalyticsP6View        = AnalyticsP6View;
window.KpiDashboard           = KpiTab;
window.ForecastView           = ForecastTab;
window.FunnelView             = PipelineTab;
window.TeamPerformanceView    = TeamTab;
